/**
 * Phase 6 ITDR (OOS) — typed SSE client for the ITDR alert stream.
 *
 * Returns a `subscribe()` function that wires an EventSource against the
 * gateway and surfaces typed `ItdrAlertEvent`s to React components. Uses
 * `EventSource` directly (no polyfill) — modern browsers support it natively.
 */

import { apiBaseUrl } from "./api";
import { getAdminJwt } from "./auth-storage";

export type ItdrAlertChannel =
  | "itdr.honeypot"
  | "itdr.decoy"
  | "itdr.session_analysis"
  | "itdr.lateral_movement"
  | "itdr.network_signal";

export type ItdrAlertSeverity = "low" | "medium" | "high" | "critical";

export interface MitreAttackMappingDto {
  techniqueId: string;
  techniqueName: string;
  tactic: string;
  tacticLabel: string;
  recommendedResponse: string;
}

export interface ItdrAlertEventDto {
  channel: ItdrAlertChannel;
  tenantId: number;
  tenantExternalId?: string;
  severity: ItdrAlertSeverity;
  trigger: string;
  payload: Record<string, unknown>;
  emittedAt: string;
  mitre?: MitreAttackMappingDto;
  externalId?: string;
  promotedIncidentExternalId?: string | null;
}

export interface ItdrAlertSubscription {
  close: () => void;
}

const ALL_CHANNELS: ItdrAlertChannel[] = [
  "itdr.honeypot",
  "itdr.decoy",
  "itdr.session_analysis",
  "itdr.lateral_movement",
  "itdr.network_signal",
];

/**
 * EventSource does not support Authorization headers, so the stream URL
 * embeds the admin JWT as `?ia_token=`. The gateway accepts that only for
 * `GET .../itdr/alerts/stream` (see `extractGovernanceJwt`).
 */
export function subscribeItdrAlerts(
  tenantExternalId: string,
  onEvent: (event: ItdrAlertEventDto) => void,
  options: {
    channels?: ItdrAlertChannel[];
    onError?: (err: Event) => void;
  } = {}
): ItdrAlertSubscription {
  const channels = options.channels ?? ALL_CHANNELS;
  const url = `${apiBaseUrl()}/tenants/${encodeURIComponent(tenantExternalId)}/itdr/alerts/stream`;
  const jwt = getAdminJwt();
  const finalUrl = jwt ? `${url}?ia_token=${encodeURIComponent(jwt)}` : url;

  const es = new EventSource(finalUrl, { withCredentials: true });

  const handler = (ev: MessageEvent) => {
    try {
      const parsed = JSON.parse(ev.data) as ItdrAlertEventDto;
      if (!channels.includes(parsed.channel)) return;
      onEvent(parsed);
    } catch {
      /* ignore malformed payloads */
    }
  };

  for (const channel of ALL_CHANNELS) {
    es.addEventListener(channel, handler as EventListener);
  }
  if (options.onError) {
    es.addEventListener("error", options.onError);
  }

  return {
    close: () => {
      for (const channel of ALL_CHANNELS) {
        es.removeEventListener(channel, handler as EventListener);
      }
      es.close();
    },
  };
}

/** Human-readable summary of an ITDR alert payload for operator surfaces. */
export function formatItdrPayloadSummary(payload: Record<string, unknown>): string {
  const bits: string[] = [];
  const push = (label: string, value: unknown) => {
    if (typeof value === "string" && value.trim()) bits.push(`${label}: ${value.trim()}`);
    else if (typeof value === "number" && Number.isFinite(value)) bits.push(`${label}: ${value}`);
  };
  push("Identity", payload.identityExternalId);
  push("Session", payload.sessionId);
  push("Source IP", payload.sourceIp ?? payload.ipAddress ?? payload.ip);
  push("New edges", payload.newEdgeCount);
  push("Confidence", payload.confidenceScore);
  push("Receipt", payload.revocationReceiptId);
  if (Array.isArray(payload.sampleNewEdges) && payload.sampleNewEdges.length > 0) {
    const edge = payload.sampleNewEdges[0] as Record<string, unknown>;
    if (typeof edge.source === "string" && typeof edge.target === "string") {
      bits.push(`Edge ${edge.source} → ${edge.target}`);
    }
  }
  return bits.length > 0 ? bits.join(" · ") : "No contextual payload recorded.";
}

/** Fetches recent ITDR alerts (REST) including MITRE ATT&CK context. */
export async function fetchItdrAlerts(
  tenantExternalId: string,
  limit = 50
): Promise<ItdrAlertEventDto[]> {
  const jwt = getAdminJwt();
  const url = `${apiBaseUrl()}/tenants/${encodeURIComponent(tenantExternalId)}/itdr/alerts?limit=${limit}`;
  const res = await fetch(url, {
    headers: jwt ? { Authorization: `Bearer ${jwt}`, "X-IA-Dashboard": "1" } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`ITDR alerts fetch failed (${res.status})`);
  const body = (await res.json()) as { items: ItdrAlertEventDto[] };
  return body.items ?? [];
}
